import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';

export function BillingPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [loadingTopup, setLoadingTopup] = useState<string | null>(null);
  const [loadingSub, setLoadingSub] = useState<string | null>(null);
  const [sharedToken, setSharedToken] = useState('');

  useEffect(() => {
    api.getProfile().then(res => {
      if (res.user) setProfile(res.user);
    }).catch(console.error);
  }, []);

  const credits = profile ? Number(profile.tokens_balance) : 0;
  const currentPlan = profile?.plan_name ?? 'Free';

  const handleTopup = async (priceId: string) => {
    setLoadingTopup(priceId);
    try {
      const res = await api.createCheckoutSession(priceId, 'topup');
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err: any) {
      alert(`Top-up failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingTopup(null);
    }
  };

  const handleSubscribe = async (priceId: string) => {
    setLoadingSub(priceId);
    try {
      const res = await api.createCheckoutSession(priceId, 'subscription');
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err: any) {
      alert(`Subscription failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingSub(null);
    }
  };

  return (
    <div className="page-container">
      <div style={{ marginBottom: '30px' }}>
        <h1 className="page-title">Billing & Usage</h1>
        <p className="page-desc">Manage your API credits, top up your balance, and upgrade your subscription plan.</p>
      </div>

      <div className="stats-grid" style={{ marginBottom: '40px' }}>
        <div className="stat-card">
          <div className="stat-card__label">Available Credits</div>
          <div className="stat-card__value" style={{ color: credits > 1000 ? 'var(--accent-green)' : 'var(--accent-orange)' }}>
            {new Intl.NumberFormat().format(credits)} <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>tokens</span>
          </div>
          <div className="stat-card__sub">Shared across all your generated API keys</div>
          <div className="stat-card__icon">🪙</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Current Plan</div>
          <div className="stat-card__value" style={{ textTransform: 'capitalize' }}>{currentPlan}</div>
          <div className="stat-card__sub">
            {currentPlan === 'Free' ? 'Limited rate limits and capabilities' : 'Priority access & higher rate limits'}
          </div>
          <div className="stat-card__icon">✨</div>
        </div>
      </div>

      <div className="card" style={{ padding: '24px', marginBottom: '40px', background: 'rgba(6, 182, 212, 0.05)', border: '1px solid var(--border-accent)' }}>
        <div className="card__title" style={{ fontSize: '18px', marginBottom: '8px', color: 'var(--accent-primary)' }}>Your Shared Token Key</div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px', lineHeight: 1.5 }}>
          Nhập Shared Token Key để dùng chung tài khoản với user trả phí (Paid User). Bạn sẽ được chia sẻ quota tokens và quyền lợi model từ tài khoản đó.
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input 
            type="text" 
            className="input" 
            placeholder="Nhập Shared Token Key..." 
            value={sharedToken}
            onChange={(e) => setSharedToken(e.target.value)}
            style={{ flex: 1, padding: '10px 14px' }}
          />
          <button 
            className="btn btn--primary" 
            onClick={() => {
              if (sharedToken) {
                // Here we would call the API to link the token
                alert(`Shared Token Key đã được ghi nhận: ${sharedToken}\n(Cần kết nối API backend)`);
              }
            }}
          >
            Lưu Token
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px' }}>
        {/* Top-up Packages */}
        <div className="card" style={{ padding: '24px' }}>
          <div className="card__title" style={{ fontSize: '18px', marginBottom: '8px' }}>Add API Credits (Top-up)</div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '24px', lineHeight: 1.5 }}>
            Purchase token credits on a pay-as-you-go basis. Credits never expire and are consumed automatically by your API keys.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Package 1 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '8px' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '16px' }}>Starter Pack</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>1,000,000 tokens</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>$5.00</div>
                <button 
                  className="btn btn--primary" 
                  onClick={() => handleTopup('price_starter')}
                  disabled={!!loadingTopup}
                >
                  {loadingTopup === 'price_starter' ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Buy'}
                </button>
              </div>
            </div>

            {/* Package 2 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '8px' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '16px' }}>Pro Pack</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>5,000,000 tokens</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>$20.00</div>
                <button 
                  className="btn btn--primary" 
                  onClick={() => handleTopup('price_pro')}
                  disabled={!!loadingTopup}
                >
                  {loadingTopup === 'price_pro' ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Buy'}
                </button>
              </div>
            </div>

            {/* Package 3 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: 'rgba(124, 58, 237, 0.05)', border: '1px solid var(--border-accent)', borderRadius: '8px' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '16px', color: 'var(--accent-primary)' }}>Enterprise Pack 🏆</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>30,000,000 tokens</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>$100.00</div>
                <button 
                  className="btn btn--primary" 
                  onClick={() => handleTopup('price_enterprise')}
                  disabled={!!loadingTopup}
                >
                  {loadingTopup === 'price_enterprise' ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Buy'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="card" style={{ padding: '24px' }}>
          <div className="card__title" style={{ fontSize: '18px', marginBottom: '8px' }}>Upgrade Plan</div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '24px', lineHeight: 1.5 }}>
            Unlock higher rate limits, priority processing, and advanced AI models for your entire team.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Team Plan */}
            <div style={{ padding: '20px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-main)' }}>Team Plan</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '4px' }}>For professional teams & startups</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '24px', fontWeight: 800 }}>$49</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>/mo</span>
                </div>
              </div>
              
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0', fontSize: '14px', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: 'var(--accent-green)' }}>✓</span> 5,000,000 bonus tokens monthly</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: 'var(--accent-green)' }}>✓</span> Priority API processing queue</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: 'var(--accent-green)' }}>✓</span> Access to premium models (GPT-4o, Claude 3.5)</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: 'var(--accent-green)' }}>✓</span> Dedicated technical support</li>
              </ul>

              <button 
                className="btn btn--primary" 
                style={{ width: '100%', padding: '12px' }}
                onClick={() => handleSubscribe('price_team_plan')}
                disabled={!!loadingSub || currentPlan === 'team' || currentPlan === 'enterprise'}
              >
                {loadingSub === 'price_team_plan' ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 
                  (currentPlan === 'team' ? 'Current Plan' : 'Subscribe to Team')}
              </button>
            </div>
          </div>

          <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: 'var(--text-muted)', fontSize: '13px' }}>
            <span>🔒 Secured by</span>
            <strong style={{ color: '#635BFF' }}>Stripe</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
